import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  SearchableCombobox,
  Skeleton,
} from "@nexus/ui";
import {
  adminPendingVendorsQueryOptions,
  adminVendorsQueryOptions,
  useLinkPendingVendor,
  useRejectPendingVendor,
} from "@/lib/queries";
import type {
  AdminVendorRow,
  PendingVendorOnboardingRow,
} from "@/features/admin/marketplace/types/admin-marketplace.types";

interface AdminPendingVendorsListProps {
  canList: boolean;
  canLink: boolean;
  canCreateVendor: boolean;
  canRemove: boolean;
  onOnboard: (row: PendingVendorOnboardingRow) => void;
}

export function AdminPendingVendorsList({
  canList,
  canLink,
  canCreateVendor,
  canRemove,
  onOnboard,
}: AdminPendingVendorsListProps) {
  const { t } = useTranslation();
  const { data: rows = [], isLoading, isError } = useQuery({
    ...adminPendingVendorsQueryOptions(),
    enabled: canList,
  });
  const { data: vendors = [] } = useQuery({
    ...adminVendorsQueryOptions(),
    enabled: canLink,
  });
  const linkMutation = useLinkPendingVendor();
  const rejectMutation = useRejectPendingVendor();
  const [linkTarget, setLinkTarget] = useState<PendingVendorOnboardingRow | null>(null);
  const [cancelTarget, setCancelTarget] = useState<PendingVendorOnboardingRow | null>(null);
  const [selectedVendorId, setSelectedVendorId] = useState<string | null>(null);

  if (!canList) {
    return (
      <p className="py-12 text-center text-sm text-muted-foreground">
        {t("common.noPermission", "You don't have permission to view this content.")}
      </p>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 4 }).map((_, index) => (
          <Skeleton key={index} className="h-14 w-full rounded-lg" />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="py-12 text-center">
        <p className="font-medium">{t("marketplace.admin.pending.errorTitle")}</p>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("errors.tryAgain", "Please try again.")}
        </p>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <EmptyState
        title={t("marketplace.admin.pending.empty")}
        description={t("marketplace.admin.pending.emptyDescription")}
      />
    );
  }

  function handleLinkConfirm() {
    if (!linkTarget || !selectedVendorId) return;
    linkMutation.mutate(
      {
        pendingId: linkTarget.id,
        vendorId: selectedVendorId,
        tenantId: linkTarget.tenantId,
      },
      {
        onSuccess: () => {
          setLinkTarget(null);
          setSelectedVendorId(null);
        },
      },
    );
  }

  function handleCancelOnboardingConfirm() {
    if (!cancelTarget) return;
    rejectMutation.mutate(cancelTarget.id, {
      onSuccess: () => {
        setCancelTarget(null);
      },
    });
  }

  return (
    <>
      <div className="flex flex-col gap-3">
        <p className="px-0.5 text-xs font-medium tabular-nums text-muted-foreground">
          {t("marketplace.admin.pending.count", { count: rows.length })}
        </p>
        <div className="overflow-hidden rounded-xl border border-border/60 bg-card">
          <table className="w-full text-sm">
            <thead className="bg-muted/40">
              <tr className="border-b border-border/50">
                <th className="px-5 py-2 text-left text-[11px] font-medium uppercase tracking-wider text-muted-foreground/70">
                  {t("marketplace.admin.pending.table.sheetLabel")}
                </th>
                <th className="hidden px-5 py-2 text-left text-[11px] font-medium uppercase tracking-wider text-muted-foreground/70 md:table-cell">
                  {t("marketplace.admin.pending.table.client")}
                </th>
                <th className="hidden px-5 py-2 text-left text-[11px] font-medium uppercase tracking-wider text-muted-foreground/70 lg:table-cell">
                  {t("marketplace.admin.pending.table.contact")}
                </th>
                <th className="px-5 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-border/50">
              {rows.map((row) => (
                <tr key={row.id} className="hover:bg-muted/40">
                  <td className="px-5 py-3.5">
                    <div className="flex flex-col gap-0.5">
                      <span className="font-medium">{row.rawLabel}</span>
                      <span className="font-mono text-xs text-muted-foreground">
                        {t("marketplace.admin.pending.importRef", {
                          importId: row.importId.slice(0, 8),
                        })}
                      </span>
                    </div>
                  </td>
                  <td className="hidden px-5 py-3.5 text-muted-foreground md:table-cell">
                    {row.tenant?.name ?? row.tenantId}
                  </td>
                  <td className="hidden px-5 py-3.5 text-muted-foreground lg:table-cell">
                    {row.contactEmail ?? "—"}
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex flex-col items-end gap-1.5">
                      <div className="flex items-start justify-end gap-2">
                        {canCreateVendor ? (
                          <Button variant="outline" size="sm" onClick={() => onOnboard(row)}>
                            {t("marketplace.admin.pending.onboardAction")}
                          </Button>
                        ) : null}
                        {canLink ? (
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => {
                              setLinkTarget(row);
                              setSelectedVendorId(null);
                            }}
                          >
                            {t("marketplace.admin.pending.linkAction")}
                          </Button>
                        ) : null}
                        {canRemove ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setCancelTarget(row)}
                          >
                            {t("marketplace.admin.pending.cancelOnboardingAction")}
                          </Button>
                        ) : null}
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog
        open={linkTarget !== null}
        onOpenChange={(open) => {
          if (!open) {
            setLinkTarget(null);
            setSelectedVendorId(null);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("marketplace.admin.pending.linkDialogTitle")}</DialogTitle>
            <DialogDescription>
              {t("marketplace.admin.pending.linkDialogDescription", {
                label: linkTarget?.rawLabel ?? "",
              })}
            </DialogDescription>
          </DialogHeader>
          <SearchableCombobox<AdminVendorRow>
            items={vendors}
            getKey={(vendor) => vendor.id}
            getLabel={(vendor) => vendor.name}
            value={selectedVendorId}
            placeholder={t("marketplace.admin.pending.linkPlaceholder")}
            searchPlaceholder={t("marketplace.admin.pending.linkSearchPlaceholder")}
            emptyMessage={t("marketplace.admin.pending.linkNoMatches")}
            onSelect={(vendor) => setSelectedVendorId(vendor.id)}
          />
          <DialogFooter>
            <Button
              type="button"
              disabled={!selectedVendorId || linkMutation.isPending}
              onClick={handleLinkConfirm}
            >
              {t("marketplace.admin.pending.linkConfirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={cancelTarget !== null}
        onOpenChange={(open) => {
          if (!open) setCancelTarget(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("marketplace.admin.pending.cancelOnboardingDialogTitle")}</DialogTitle>
            <DialogDescription>
              {t("marketplace.admin.pending.cancelOnboardingDialogDescription", {
                label: cancelTarget?.rawLabel ?? "",
              })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setCancelTarget(null)}
            >
              {t("common.cancel", "Cancel")}
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={rejectMutation.isPending}
              onClick={handleCancelOnboardingConfirm}
            >
              {rejectMutation.isPending
                ? t("marketplace.admin.pending.cancelOnboardingPending")
                : t("marketplace.admin.pending.cancelOnboardingConfirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
